import React, { createContext } from 'react'
import { BaseContextState, BaseContextProvider } from '../BaseContext'
import IShelterSetting from '../../interfaces/IShelterSetting'
import {
  SearchRequest,
  ShelterSettingKennelCardSaveRequest,
  ShelterSettingIframeSaveRequest,
  ShelterSettingAdoptionSaveRequest,
  ShelterSettingSponsorSaveRequest,
  ShelterSettingAdoptionPathSaveRequest,
  ShelterSettingKioskSaveRequest,
  ShelterSettingInPersonSaveRequest,
  ShelterSettingAdopterPersonalDocumentsSaveRequest,
  ShelterSettingPetProfileSaveRequest,
  ShelterSettingDiscountReasonSaveRequest
} from '../../services/api/interfaces/ApiRequest'
import { 
  shelterSettingList,
  shelterSettingKennelCardSave,
  shelterSettingIframeSave,
  shelterSettingAdoptionSave,
  shelterSettingSponsorSave,
  shelterSettingAdoptionPathSave,
  shelterSettingKioskSave,
  shelterSettingInPersonSave,
  shelterSettingAdopterPersonalDocumentsSave,
  shelterSettingPetProfileSave,
  shelterSettingDiscountReasonSave
} from '../../services/api'

interface ShelterSettingContextState extends BaseContextState {
  shelterSettings: Array<IShelterSetting> | null,
  shelterSetting: IShelterSetting | null
}

export interface ShelterSettingContextData extends ShelterSettingContextState {
  resetErrors: Function,

  requestShelterSettingList: (args: SearchRequest) => Promise<any>,
  requestShelterSettingFind: (args: SearchRequest) => Promise<any>,
  requestShelterSettingKennelCardSave: (args: ShelterSettingKennelCardSaveRequest) => void,
  requestShelterSettingIframeSave: (args: ShelterSettingIframeSaveRequest) => void,
  requestShelterSettingAdoptionSave: (args: ShelterSettingAdoptionSaveRequest) => Promise<any>,
  requestShelterSettingSponsorSave: (args: ShelterSettingSponsorSaveRequest) => Promise<any>,
  requestShelterSettingAdoptionPathSave: (args: ShelterSettingAdoptionPathSaveRequest) => Promise<any>,
  requestShelterSettingKioskSave: (args: ShelterSettingKioskSaveRequest) => Promise<any>,
  requestShelterSettingInPersonSave: (args: ShelterSettingInPersonSaveRequest) => Promise<any>,
  requestShelterSettingAdopterPersonalDocumentsSave: (args: ShelterSettingAdopterPersonalDocumentsSaveRequest) => Promise<any>,
  requestShelterSettingPetProfileSave: (args: ShelterSettingPetProfileSaveRequest) => Promise<any>,
  requestShelterSettingDiscountReasonSave: (args: ShelterSettingDiscountReasonSaveRequest) => Promise<any>,
}

// Create context
const { Provider, Consumer } = createContext({} as ShelterSettingContextData)

// Export consumer
export const ShelterSettingConsumer = Consumer

export class ShelterSettingProvider extends BaseContextProvider {
  state: ShelterSettingContextState = {
    shelterSettings: null,
    shelterSetting: null
  }

  /**
   * Make api request for shelter setting list
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterSettingList = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterSettingList(payload)

        // Process our response
        this.processResponse(response, { 
          paginate: {
            destination: 'shelterSettings'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter setting finder
   * 
   * @param {SearchRequest} payload 
   */
  requestShelterSettingFind = (payload: SearchRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null,
        shelterSetting: null
      }, async () => {
        payload.options = {
          ...payload.options,
          _single: true
        }
  
        const response = await shelterSettingList(payload)
  
        // Process our response
        this.processResponse(response, { 
          mappings: {
            destination: 'shelterSetting'
          },
        })

        if (this.state.success) {
          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter setting kennel card save
   * 
   * @param {ShelterSettingKennelCardSaveRequest} payload 
   */
  requestShelterSettingKennelCardSave = (payload: ShelterSettingKennelCardSaveRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      let response = await shelterSettingKennelCardSave(payload) as any

      // Refetch our shelter settings
      if (this.state.success) {
        const uuid = payload.shelter_uuid
        const newPayload = {
          search: {
            organization_shelter: {
              has: {
                uuid
              }
            }
          }
        } as SearchRequest
        response = await shelterSettingList(newPayload)
      }

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'shelterSettings'
        },
      })
    })
  }

  /**
   * Make api request for shelter setting iframe save
   * 
   * @param {ShelterSettingIframeSaveRequest} payload 
   */
  requestShelterSettingIframeSave = (payload: ShelterSettingIframeSaveRequest) => {
    this.setState({
      fetching: true,
      error: null
    }, async () => {
      let response = await shelterSettingIframeSave(payload) as any

      // Refetch our shelter settings
      if (this.state.success) {
        const uuid = payload.shelter_uuid
        const newPayload = {
          search: {
            organization_shelter: {
              has: {
                uuid
              }
            }
          }
        } as SearchRequest
        response = await shelterSettingList(newPayload)
      }

      // Process our response
      this.processResponse(response, { 
        paginate: {
          destination: 'shelterSettings'
        },
      })
    })
  }

   /**
   * Make api request for shelter setting pet profile save
   * 
   * @param {ShelterSettingPetProfileSaveRequest} payload 
   */
    requestShelterSettingPetProfileSave = (payload: ShelterSettingPetProfileSaveRequest) => {
      return new Promise((resolve, reject) => {
        this.setState({
          fetching: true,
          error: null
        }, async () => {
          const response = await shelterSettingPetProfileSave(payload) as any
          this.processResponse(response)
    
          // Refetch our shelter settings
          if (this.state.success) {
            this.requestShelterSettingList({
              search: {
                organization_shelter: {
                  has: {
                    uuid: payload.shelter_uuid
                  }
                }
              }
            })
  
            resolve(true)
          } else {
            reject(this.state.error)
          }
        })
      })
    }

     /**
   * Make api request for shelter setting pet profile save
   * 
   * @param {ShelterSettingPetProfileSaveRequest} payload 
   */
     requestShelterSettingDiscountReasonSave = (payload: ShelterSettingDiscountReasonSaveRequest) => {
      return new Promise((resolve, reject) => {
        this.setState({
          fetching: true,
          error: null
        }, async () => {
          const response = await shelterSettingDiscountReasonSave(payload) as any
          this.processResponse(response)
    
          // Refetch our shelter settings
          if (this.state.success) {
            this.requestShelterSettingList({
              search: {
                organization_shelter: {
                  has: {
                    uuid: payload.shelter_uuid
                  }
                }
              }
            })
  
            resolve(true)
          } else {
            reject(this.state.error)
          }
        })
      })
    }
  
  /**
   * Make api request for shelter setting adoption save
   * 
   * @param {ShelterSettingAdoptionSaveRequest} payload 
   */
  requestShelterSettingAdoptionSave = (payload: ShelterSettingAdoptionSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterSettingAdoptionSave(payload) as any
        this.processResponse(response)
  
        // Refetch our shelter settings
        if (this.state.success) {
          this.requestShelterSettingList({
            search: {
              organization_shelter: {
                has: {
                  uuid: payload.shelter_uuid
                }
              }
            }
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter setting sponsor save
   * 
   * @param {ShelterSettingSponsorSaveRequest} payload 
   */
  requestShelterSettingSponsorSave = (payload: ShelterSettingSponsorSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterSettingSponsorSave(payload) as any
        this.processResponse(response)
  
        // Refetch our shelter settings
        if (this.state.success) {
          this.requestShelterSettingList({
            search: {
              organization_shelter: {
                has: {
                  uuid: payload.shelter_uuid
                }
              }
            }
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request to save adoption path massages
   * 
   * @param {ShelterSettingAdoptionPathSaveRequest} payload
  */
 requestShelterSettingAdoptionPathSave = (payload: ShelterSettingAdoptionPathSaveRequest) => {
   return new Promise((resolve, reject) => {
     this.setState({
       fetching: true,
       error: null
     }, async () => {
       const response = await shelterSettingAdoptionPathSave(payload) as any
       this.processResponse(response)

       // Refetch our shelter settings
       if (this.state.success) {
         this.requestShelterSettingList({
           search: {
             organization_shelter: {
               has: {
                 uuid: payload.shelter_uuid
               }
             }
           }
         })

         resolve(true)
       } else {
         reject(this.state.error)
       }
     })
   })
 }

  /**
   * Make api request for shelter setting kiosk save
   * 
   * @param {ShelterSettingKioskSaveRequest} payload 
   */
  requestShelterSettingKioskSave = (payload: ShelterSettingKioskSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterSettingKioskSave(payload) as any
        this.processResponse(response)
  
        // Refetch our shelter settings
        if (this.state.success) {
          this.requestShelterSettingList({
            search: {
              organization_shelter: {
                has: {
                  uuid: payload.shelter_uuid
                }
              }
            }
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter setting in-person save
   * 
   * @param {ShelterSettingInPersonSaveRequest} payload 
   */
  requestShelterSettingInPersonSave = (payload: ShelterSettingInPersonSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterSettingInPersonSave(payload) as any
        this.processResponse(response)
  
        // Refetch our shelter settings
        if (this.state.success) {
          this.requestShelterSettingList({
            search: {
              organization_shelter: {
                has: {
                  uuid: payload.shelter_uuid
                }
              }
            }
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  /**
   * Make api request for shelter setting adopter personal documents save
   * 
   * @param {ShelterSettingAdopterPersonalDocumentsSaveRequest} payload 
   */
   requestShelterSettingAdopterPersonalDocumentsSave = (payload: ShelterSettingAdopterPersonalDocumentsSaveRequest) => {
    return new Promise((resolve, reject) => {
      this.setState({
        fetching: true,
        error: null
      }, async () => {
        const response = await shelterSettingAdopterPersonalDocumentsSave(payload) as any
        this.processResponse(response)
  
        // Refetch our shelter settings
        if (this.state.success) {
          this.requestShelterSettingList({
            search: {
              organization_shelter: {
                has: {
                  uuid: payload.shelter_uuid
                }
              }
            }
          })

          resolve(true)
        } else {
          reject(this.state.error)
        }
      })
    })
  }

  render() {
    return (
      <Provider
        value={{
          // States
          ...this.state,

          // Dispatches
          resetErrors: this.resetErrors,
          requestShelterSettingList: this.requestShelterSettingList,
          requestShelterSettingFind: this.requestShelterSettingFind,
          requestShelterSettingKennelCardSave: this.requestShelterSettingKennelCardSave,
          requestShelterSettingIframeSave: this.requestShelterSettingIframeSave,
          requestShelterSettingAdoptionSave: this.requestShelterSettingAdoptionSave,
          requestShelterSettingSponsorSave: this.requestShelterSettingSponsorSave,
          requestShelterSettingAdoptionPathSave: this.requestShelterSettingAdoptionPathSave,
          requestShelterSettingKioskSave: this.requestShelterSettingKioskSave,
          requestShelterSettingInPersonSave: this.requestShelterSettingInPersonSave,
          requestShelterSettingAdopterPersonalDocumentsSave: this.requestShelterSettingAdopterPersonalDocumentsSave,
          requestShelterSettingPetProfileSave: this.requestShelterSettingPetProfileSave,
          requestShelterSettingDiscountReasonSave: this.requestShelterSettingDiscountReasonSave,
        }}
      >
        {this.props.children}
      </Provider>
    )
  }
}
